/*
* Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
*/
import React, {memo, useCallback} from 'react';
import MaterialTab from '@material-ui/core/Tab';
import classnames from 'classnames';
import {GenericObject} from '@ion/core/src/types/entities';
import useExtendStyles from '../../../hooks/useExtendStyles';
import {dlsTabIndicatorBorder} from '../../../theme/helpers';
import {dlsColor, dlsTypography} from '../../../theme/theme';
import useThemeContext from '../../../theme/useThemeContext';
import {DLSTheme} from '../../../types/types';
import StableButtonLabel from '../../buttons/current/StableButtonLabel';
import TabIndicator, {TabIndicatorProps} from './TabIndicator';

// defining props definition for Tab component...
export interface TabProps {
  tab?: {
    tabHeader: string,
    path?: string,
    tag?: string,
    badgeCount?: number,
    hasAlert?: boolean,
    indicator?: TabIndicatorProps
  };
  onClick?: Function;
  index?: number;
  selected?: boolean;
  overrides?: (theme: DLSTheme) => GenericObject;
  materialOverrides?: (theme: DLSTheme) => GenericObject;
  hideBackground?: boolean;
};

const getBadgeCircleStyles = (size = 18) => ({
  width          : `${size}px`,
  height         : `${size}px`,
  boxSizing      : 'border-box',
  display        : 'flex',
  flexShrink     : 0,
  alignItems     : 'center',
  justifyContent : 'center',
  borderRadius   : '100px'
} as const);

const badgeTextStyles = {
  fontSize      : '10px',
  textAlign     : 'center',
  fontFamily    : 'Inter',
  fontWeight    : 600,
  lineHeight    : '12px',
  letterSpacing : '0px',
  textTransform : 'uppercase'
} as const;

// defining the style definition for Tab component...
const getStyles = (isCurrentTheme: boolean) => () => {
  return {
    root : {
      fontSize      : '14px',
      fontFamily    : isCurrentTheme ? 'var(--primary-font-family)' : 'Roboto',
      lineHeight    : isCurrentTheme ? '20px' : '16px',
      letterSpacing : '0.5px',
      textTransform : 'capitalize'
    },
    backgroundStyle : {
      backgroundColor : dlsColor('navBarGrey')
    },
    altBackgroundStyle : {
      backgroundColor : dlsColor('navBarGrey')
    },
    endAdornment : {
      position : 'absolute',
      right    : 0
    },
    tag : {
      backgroundColor : dlsColor('caBlue'),
      borderRadius    : '8px',
      display         : 'flex',
      padding         : '4px 8px 4px 8px'
    },
    tabLabelContainer : {
      gap            : '8px',
      width          : 'fit-content',
      display        : 'flex',
      alignItems     : 'center',
      justifyContent : 'center'
    },
    badgeGroup : {
      gap        : '4px',
      display    : 'flex',
      alignItems : 'center'
    },
    countBadge : {
      ...getBadgeCircleStyles(18),
      backgroundColor : dlsColor('grey5'),
      border          : `1px solid ${dlsColor('grey5')}`
    },
    countBadgeText : {
      ...badgeTextStyles,
      color : dlsColor('grey1')
    },
    alertDot : {
      ...getBadgeCircleStyles(9),
      backgroundColor : dlsColor('red')
    }
  };
};

const getMaterialTabStyles = (isCurrentTheme: boolean) => () => {
  const {fontWeight: label2FontWeight} = dlsTypography('label2') || {};
  const currentStates = isCurrentTheme ? {
    '&:focus-visible' : {
      outline : 'none'
    },
    '&:focus-visible $materialWrapper' : {
      boxShadow : `inset 0 0 0 2px ${dlsColor('skyBlue')}`
    },
    '&:hover' : {
      fontWeight : 700
    },
    '&:hover $materialWrapper' : {
      backgroundColor : dlsColor('grey6')
    },
    '&:active' : {
      color        : dlsColor('darkTeal'),
      fontWeight   : 700,
      borderBottom : `${dlsTabIndicatorBorder('selected')} !important`
    },
    '&:active $materialWrapper' : {
      backgroundColor : dlsColor('aquamarine')
    }
  } : {};
  return {
    materialRoot : {
      color                   : dlsColor(isCurrentTheme ? 'grey2' : 'grey1'),
      height                  : '44px',
      padding                 : '4px 0 0',
      minHeight               : '44px',
      fontWeight              : 500,
      boxSizing               : 'border-box',
      borderTopLeftRadius     : '8px !important',
      borderTopRightRadius    : '8px !important',
      borderBottomLeftRadius  : '0 !important',
      borderBottomRightRadius : '0 !important',
      borderBottom            : `${dlsTabIndicatorBorder('resting')} !important`,
      ...currentStates,
      '&$materialSelected'    : {
        color        : dlsColor('darkTeal'),
        fontWeight   : isCurrentTheme ? label2FontWeight : 600,
        borderBottom : `${dlsTabIndicatorBorder('selected')} !important`
      },
      '&$materialSelected $materialWrapper' : {
        backgroundColor : dlsColor('paleBlue')
      }
    },
    materialSelected : {},
    materialWrapper  : {
      display        : 'flex',
      width          : '100% !important',
      height         : '32px',
      padding        : '0 12px',
      overflow       : 'hidden',
      boxSizing      : 'border-box',
      alignItems     : 'center',
      whiteSpace     : 'nowrap',
      borderRadius   : isCurrentTheme ? '8px 8px 0 0' : '8px',
      textOverflow   : 'ellipsis',
      justifyContent : 'center'
    }
  };
};

/**
 * Defines the tab component. This component is reusable and can be custom
 * rendered with props.
 *
 * ```typescript
 *
 *  import {Tab} from '@ion/dls/src/components/tabs';
 *
 *  <Tab tab={tab} index={index} />
 *
 *  // with a count badge and an alert dot (e.g. the Tags accordion's
 *  // Assigned/In Range tab bar) ...
 *  <Tab tab={{tabHeader: 'Assigned', badgeCount: 2, hasAlert: true}}
 *    index={index} />
 *
 * ```
 */
export const Tab = ({
  tab,
  onClick,
  selected,
  index = 0,
  overrides,
  materialOverrides,
  hideBackground = false
}: TabProps) => {
  const {
    isCurrent : isCurrentTheme
  } = useThemeContext();

  const tabViewStyles     = useExtendStyles(getStyles(isCurrentTheme), overrides);
  const classes           = tabViewStyles();
  const materialTabStyles = useExtendStyles(getMaterialTabStyles(isCurrentTheme), materialOverrides);
  const materialClasses   = materialTabStyles();

  const onChangeIndex = useCallback((e) => {
    onClick?.(e, index);
  }, [index, onClick]);

  const hasBadge = typeof tab?.badgeCount === 'number' || Boolean(tab?.hasAlert);

  return (
    <MaterialTab className={
      classnames(
        classes.root,
        !hideBackground && classes.backgroundStyle
      )}
    key={`${index}-${tab?.tabHeader}`}
    classes={{
      root     : materialClasses.materialRoot,
      selected : materialClasses.materialSelected,
      wrapper  : materialClasses.materialWrapper
    }}
    onClick={onChangeIndex}
    selected={selected}
    label={
      <div className={classes.tabLabelContainer}>
        {isCurrentTheme ? (
          <StableButtonLabel reserveFontWeight={700}>
            {tab?.tabHeader}
          </StableButtonLabel>
        ) : tab?.tabHeader}
        {isCurrentTheme && tab?.indicator && <TabIndicator {...tab.indicator} />}
        {hasBadge
          ? (
            <span className={classes.badgeGroup}>
              {typeof tab?.badgeCount === 'number' && (
                <span className={classes.countBadge}>
                  <span className={classes.countBadgeText}>
                    {tab.badgeCount}
                  </span>
                </span>
              )}
              {tab?.hasAlert && <span className={classes.alertDot} />}
            </span>
          )
          : tab?.tag?.split('').map(() => <>&nbsp;</>)}
      </div>
    } />
  );
};

export default memo(Tab);
